import type { TovMapAttemptSummaryResponse } from "contracts/api/client-tov/mapping-attempts";
import { createSignal, Show } from "solid-js";
import { TOV_MAPPING_STATE } from "../../enums/tov";
import { isAdmin } from "../../store/admin";
import { errorMessage } from "../../store/api-client";
import {
  checkpointProgressLabel,
  isMappingFailed,
  type MappingState,
} from "../tov/mapAttemptState";
import { requestMapping } from "../tov/useMapAttempt";

// Setup/recovery surface shown when the profile has no ACTIVE version yet: the
// first generated draft is still mapping, or its mapping failed and is waiting
// to be resumed. Replaces the old behavior of rendering the active-guidance
// layer with "Processing"/"Not available" placeholders forever.
export function BrandGuidelinesSetupPanel(props: {
  websiteId: string;
  clientId: string | undefined;
  versionId: string | undefined;
  state: MappingState;
  summary: TovMapAttemptSummaryResponse | null;
  sourceCount: number | null;
}) {
  const [resuming, setResuming] = createSignal(false);
  const [error, setError] = createSignal("");
  const failureReason = () =>
    props.summary?.attempt?.failureReason ?? "No failure detail was recorded.";
  const progress = () => checkpointProgressLabel(props.summary);

  const resume = async () => {
    const clientId = props.clientId;
    const versionId = props.versionId;
    if (!clientId || !versionId) return;
    setError("");
    setResuming(true);
    try {
      await requestMapping(clientId, versionId);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setResuming(false);
    }
  };

  const heading = () => {
    switch (props.state) {
      case TOV_MAPPING_STATE.RUNNING:
        return "Drafting brand guidance from your sources";
      case TOV_MAPPING_STATE.FAILED_RESUMABLE:
      case TOV_MAPPING_STATE.FAILED_RESTART:
        return "Guideline mapping failed";
      case TOV_MAPPING_STATE.INCOMPATIBLE:
        return "The failed mapping run can no longer be resumed";
      case TOV_MAPPING_STATE.SUCCEEDED:
        return "Mapping completed";
      default:
        return props.sourceCount === 0
          ? "No brand guidance yet"
          : "Preparing your brand guidance";
    }
  };
  const body = () => {
    switch (props.state) {
      case TOV_MAPPING_STATE.RUNNING:
        return "A mapping run is extracting guidance from your uploaded sources. This page updates automatically when it finishes.";
      case TOV_MAPPING_STATE.FAILED_RESUMABLE:
        return "Successful extraction work was saved. Resuming reruns only the missing or failed page groups; retained checkpoints are reused.";
      case TOV_MAPPING_STATE.FAILED_RESTART:
        return "The run failed before any progress could be saved, so a new mapping run starts from scratch.";
      case TOV_MAPPING_STATE.INCOMPATIBLE:
        return "The mapper changed since the failed run, so a fresh mapping run is needed.";
      case TOV_MAPPING_STATE.SUCCEEDED:
        return "The generated guidance is being loaded.";
      default:
        return props.sourceCount === 0
          ? "Upload brand documents in the guideline workspace to generate guidance for assessments."
          : "Uploaded sources are still being processed. Mapping starts automatically once they are embedded.";
    }
  };
  const actionLabel = () =>
    props.state === TOV_MAPPING_STATE.FAILED_RESUMABLE
      ? "Resume mapping"
      : "Start mapping run";

  return (
    <section class="signal-panel signal-empty-state">
      <span class="signal-mini-kicker">Guideline setup</span>
      <h2>{heading()}</h2>
      <p>{body()}</p>
      <Show
        when={
          isMappingFailed(props.state) ||
          props.state === TOV_MAPPING_STATE.INCOMPATIBLE
        }
      >
        <p class="gate-error" role="alert">
          {failureReason()}
        </p>
      </Show>
      <Show when={progress()}>
        {(label) => <p class="signal-muted">{label()}</p>}
      </Show>
      <Show
        when={
          isMappingFailed(props.state) ||
          props.state === TOV_MAPPING_STATE.INCOMPATIBLE
        }
      >
        <Show
          when={isAdmin()}
          fallback={
            <p class="signal-muted">
              An administrator can resume mapping from the guideline workspace.
            </p>
          }
        >
          <div class="signal-empty-actions">
            <button
              type="button"
              class="btn btn-primary"
              disabled={resuming()}
              onClick={() => void resume()}
            >
              {resuming() ? "Requesting…" : actionLabel()}
            </button>
            <a
              class="signal-inline-link"
              href={`/w/${props.websiteId}/scan/brand-guidelines/manage`}
            >
              Open guideline workspace
            </a>
          </div>
        </Show>
      </Show>
      <Show when={error()}>
        <p class="gate-error">{error()}</p>
      </Show>
    </section>
  );
}
